import * as XLSX from 'xlsx';
import { downloadBlob, exportFileStamp } from './exportExcel';

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function errorText(row) {
  if (Array.isArray(row?.errors)) {
    return row.errors
      .filter((e) => e != null && String(e).trim() !== '')
      .map(String)
      .join('; ');
  }
  if (row?.errors == null || row?.errors === '') return '';
  return String(row.errors);
}

function cell(value) {
  if (value == null) return '';
  return value;
}

function listCell(value) {
  if (Array.isArray(value)) return value.filter((v) => v != null && v !== '').join(';');
  if (value == null) return '';
  return String(value);
}

function sheetFromObjects(rows, headers) {
  return XLSX.utils.json_to_sheet(rows, { header: headers });
}

function writeAndDownload(workbook, fileName) {
  if (!workbook?.SheetNames?.length) {
    throw new Error('No error rows to download');
  }
  const buf = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(blob, fileName);
}

const BU_HEADERS = [
  'shortName',
  'fullNameGst',
  'address1',
  'address2',
  'locality',
  'sublocality',
  'taluka',
  'district',
  'state',
  'country',
  'pin',
  'latitude',
  'longitude',
  'villageCode',
  'villageName',
  'isActive',
  'error',
];

const SITE_HEADERS = [
  'buShortName',
  'siteName',
  'projectOperation',
  'address1',
  'address2',
  'locality',
  'sublocality',
  'taluka',
  'district',
  'state',
  'country',
  'pin',
  'latitude',
  'longitude',
  'villageCode',
  'villageName',
  'pinOverride',
  'isActive',
  'error',
];

const USER_HEADERS = [
  'phone',
  'aadhaarNumber',
  'panNumber',
  'email',
  'firstName',
  'lastName',
  'middleName',
  'gender',
  'dob',
  'address1',
  'address2',
  'village',
  'taluka',
  'district',
  'state',
  'pin',
  'tradeName',
  'gstNumber',
  'companyEmail',
  'constitution',
  'employeeCode',
  'offerings',
  'specialServices',
  'buyerVerified',
  'sellerVerified',
  'isActive',
  'error',
];

const USER_LINK_HEADERS = ['phone', 'buShortName', 'siteName', 'isPrimary', 'error'];

const USER_EQUIPMENT_HEADERS = [
  'phone',
  'equipmentTypeName',
  'manufacturer',
  'model',
  'variant',
  'fuel',
  'color',
  'regNo',
  'chassisNumber',
  'engineNumber',
  'capacity',
  'capacityUom',
  'yom',
  'nameAsPerRc',
  'description',
  'rcValidUntil',
  'pucValidUntil',
  'insuranceValidUntil',
  'fitnessValidUntil',
  'taxValidUntil',
  'isActive',
  'error',
];

function yn(value, whenNull = 'Yes') {
  if (value === false) return 'No';
  if (value === true) return 'Yes';
  return whenNull;
}

export function downloadBuSitesImportErrorRows(input) {
  const { buErrorRows = [], siteErrorRows = [] } = input || {};
  const buRows = asArray(buErrorRows);
  const siteRows = asArray(siteErrorRows);
  if (!buRows.length && !siteRows.length) {
    throw new Error('No error rows to download');
  }
  const workbook = XLSX.utils.book_new();

  if (buRows.length) {
    const rows = buRows.map((row) => {
      const d = row?.data || {};
      return {
        shortName: cell(d.shortName),
        fullNameGst: cell(d.fullNameGst),
        address1: cell(d.address1),
        address2: cell(d.address2),
        locality: cell(d.locality),
        sublocality: cell(d.sublocality),
        taluka: cell(d.taluka),
        district: cell(d.district),
        state: cell(d.state),
        country: cell(d.country),
        pin: cell(d.pin),
        latitude: cell(d.latitude),
        longitude: cell(d.longitude),
        villageCode: cell(d.villageCode),
        villageName: cell(d.villageName),
        isActive: yn(d.isActive),
        error: errorText(row),
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      sheetFromObjects(rows, BU_HEADERS),
      'BusinessUnits',
    );
  }

  if (siteRows.length) {
    const rows = siteRows.map((row) => {
      const d = row?.data || {};
      return {
        buShortName: cell(d.buShortName),
        siteName: cell(d.siteName),
        projectOperation: cell(d.projectOperation),
        address1: cell(d.address1),
        address2: cell(d.address2),
        locality: cell(d.locality),
        sublocality: cell(d.sublocality),
        taluka: cell(d.taluka),
        district: cell(d.district),
        state: cell(d.state),
        country: cell(d.country),
        pin: cell(d.pin),
        latitude: cell(d.latitude),
        longitude: cell(d.longitude),
        villageCode: cell(d.villageCode),
        villageName: cell(d.villageName),
        pinOverride: cell(d.pinOverride),
        isActive: yn(d.isActive),
        error: errorText(row),
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      sheetFromObjects(rows, SITE_HEADERS),
      'Sites',
    );
  }

  writeAndDownload(
    workbook,
    `bu_sites_import_errors_${exportFileStamp()}.xlsx`,
  );
}

export function downloadUsersImportErrorRows(input) {
  const { userErrorRows = [], linkErrorRows = [] } = input || {};
  const userRows = asArray(userErrorRows);
  const linkRows = asArray(linkErrorRows);
  if (!userRows.length && !linkRows.length) {
    throw new Error('No error rows to download');
  }
  const workbook = XLSX.utils.book_new();

  if (userRows.length) {
    const rows = userRows.map((row) => {
      const d = row?.data || {};
      const role = d.role || '';
      return {
        phone: cell(d.phone),
        aadhaarNumber: cell(d.aadhaarNumber),
        panNumber: cell(d.panNumber),
        email: cell(d.email),
        firstName: cell(d.firstName),
        lastName: cell(d.lastName),
        middleName: cell(d.middleName),
        gender: cell(d.gender),
        dob: cell(d.dob),
        address1: cell(d.address1),
        address2: cell(d.address2),
        village: cell(d.village),
        taluka: cell(d.taluka),
        district: cell(d.district),
        state: cell(d.state),
        pin: cell(d.pin),
        tradeName: cell(d.tradeName),
        gstNumber: cell(d.gstNumber),
        companyEmail: cell(d.companyEmail),
        constitution: cell(d.constitution),
        employeeCode: cell(d.employeeCode),
        offerings: listCell(d.offerings),
        specialServices: listCell(d.specialServices),
        buyerVerified:
          d.buyerVerificationStatus === 'verified'
            ? 'Yes'
            : role === 'agency' || role === 'both'
              ? 'No'
              : '',
        sellerVerified:
          d.sellerVerificationStatus === 'verified'
            ? 'Yes'
            : role === 'service_provider' || role === 'both'
              ? 'No'
              : '',
        isActive: yn(d.isActive),
        error: errorText(row),
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      sheetFromObjects(rows, USER_HEADERS),
      'Users',
    );
  }

  if (linkRows.length) {
    const rows = linkRows.map((row) => {
      const d = row?.data || {};
      return {
        phone: cell(d.phone),
        buShortName: cell(d.buShortName),
        siteName: cell(d.siteName),
        isPrimary: yn(d.isPrimary, 'No'),
        error: errorText(row),
      };
    });
    XLSX.utils.book_append_sheet(
      workbook,
      sheetFromObjects(rows, USER_LINK_HEADERS),
      'BuyerBusinessUnits',
    );
  }

  writeAndDownload(
    workbook,
    `users_import_errors_${exportFileStamp()}.xlsx`,
  );
}

export function downloadUserEquipmentImportErrorRows(errorRows) {
  const rowsIn = asArray(errorRows);
  if (!rowsIn.length) {
    throw new Error('No error rows to download');
  }
  const rows = rowsIn.map((row) => {
    const d = row?.data || {};
    return {
      phone: cell(d.phone),
      equipmentTypeName: cell(d.equipmentTypeName),
      manufacturer: cell(d.manufacturer),
      model: cell(d.model),
      variant: cell(d.variant),
      fuel: cell(d.fuel),
      color: cell(d.color),
      regNo: cell(d.regNo),
      chassisNumber: cell(d.chassisNumber),
      engineNumber: cell(d.engineNumber),
      capacity: cell(d.capacity),
      capacityUom: cell(d.capacityUom),
      yom: cell(d.yom),
      nameAsPerRc: cell(d.nameAsPerRc),
      description: cell(d.description),
      rcValidUntil: cell(d.rcValidUntil),
      pucValidUntil: cell(d.pucValidUntil),
      insuranceValidUntil: cell(d.insuranceValidUntil),
      fitnessValidUntil: cell(d.fitnessValidUntil),
      taxValidUntil: cell(d.taxValidUntil),
      isActive: yn(d.isActive),
      error: errorText(row),
    };
  });
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromObjects(rows, USER_EQUIPMENT_HEADERS),
    'UserEquipment',
  );
  writeAndDownload(
    workbook,
    `user_equipment_import_errors_${exportFileStamp()}.xlsx`,
  );
}
